import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';

import Text from 'components/shared/Text';
import styles from './style.module.css';
import { infoThemeIcon, favoriteIconOrange, favoriteIconBlack } from 'assets/newIcons';
import MobileContext from 'context/MobileContext';
import SmallMobileContext from 'context/SmallMobileContext';
import LargeMobileContext from 'context/LargeMobile';
import { Modal, Box } from '@material-ui/core';

const LocationSelectionStep = ({ onNext, stepData, previousSelection, canSkip }) => {
  const isMobile = useContext(MobileContext);
  const isSmallMobile = useContext(SmallMobileContext);
  const isLargeMobile = useContext(LargeMobileContext);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [infoModalOpen, setInfoModalOpen] = useState(false);

  // Initialize selected location from API data or previous state
  useEffect(() => {
    let initialSelection = '';

    // Priority 1: Check if we have current session data (user made changes in this session)
    if (previousSelection && previousSelection !== stepData?.hubspotSkipValue) {
      initialSelection = previousSelection;
    }
    // Priority 2: Check if user has already answered this step from API (browser refresh scenario only)
    else if (stepData?.hasAnswered && stepData?.userResponse) {
      // userResponse comes as an array, extract the first value
      let responseValue = Array.isArray(stepData.userResponse)
        ? stepData.userResponse[0]
        : stepData.userResponse;

      // Check if the response is the hubspotSkipValue (meaning user skipped)
      const isSkippedValue =
        stepData.hubspotSkipValue && responseValue === stepData.hubspotSkipValue;

      // Only use the value if it's not the skip value
      if (!isSkippedValue) {
        initialSelection = responseValue;
      }
    }

    if (initialSelection) {
      setSelectedLocation(initialSelection);
    }
  }, [
    stepData?.hasAnswered,
    stepData?.userResponse,
    stepData?.hubspotSkipValue,
    previousSelection,
  ]);

  // Parse address from subLabel for mobile view
  const parseAddress = subLabel => {
    if (!subLabel) return { street: '', city: '' };

    // Split by comma - typically "Street Address, City"
    const parts = subLabel.split(',');
    if (parts.length >= 2) {
      return {
        street: parts[0].trim(),
        city: parts[1].trim(),
      };
    }

    // If no comma, return full address as street
    return {
      street: subLabel.trim(),
      city: '',
    };
  };

  // Get location data from API
  const getLocationData = () => {
    if (!stepData?.options) return [];

    // Map API options to location format
    return stepData.options.map((option, index) => {
      // Parse address from API's subLabel
      const addressInfo = parseAddress(option.subLabel);

      return {
        // Use API data as primary source
        id: option.value, // Use API value as unique identifier
        displayName: option.label,
        mediaUrl: option.mediaUrl,
        subMediaUrl: option.subMediaUrl, // New hover/location icon
        value: option.value,
        displayOrder: option.displayOrder,
        subLabel: option.subLabel, // Full address string

        // Parsed address for mobile view
        streetC: addressInfo.street,
        cityC: addressInfo.city,
      };
    });
  };

  const locationData = getLocationData();

  const handleSelectLocation = locationId => {
    setSelectedLocation(locationId);
  };

  const handleMouseEnter = index => {
    setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    setHoveredIndex(-1);
  };

  const handleInfoClick = () => {
    setInfoModalOpen(true);
  };

  const handleCloseInfoModal = () => {
    setInfoModalOpen(false);
  };

  const handleNextClick = () => {
    if (onNext) {
      // If we're using the multi-step onboarding flow
      onNext(selectedLocation);
    }
  };

  const handleSkipClick = () => {
    if (onNext) {
      // Clear current selection when skipping
      setSelectedLocation('');
      // Use hubspotSkipValue from API if available
      onNext(stepData?.hubspotSkipValue);
    }
  };

  // Validation function based on API minInput/maxInput
  const isNextButtonEnabled = () => {
    const minInput = stepData?.validations?.minInput;
    const maxInput = stepData?.validations?.maxInput;

    // Check if user has selected something (selectedLocation is a string/ID)
    const hasSelection = selectedLocation && selectedLocation.trim() !== '';

    // For single selection (location), minInput of 1 means at least one selection required
    if (minInput === 1) {
      return hasSelection;
    }

    // If minInput is 0, allow empty selection (though unlikely for location)
    return true;
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'white',
    padding: '25px',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
    borderRadius: '20px',
    width: isMobile ? '250px' : '275px',
    maxWidth: '500px',
    outline: 'none',
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerContainer}>
        <div className={styles.headerTextWrapper}>
          <Text
            tag="h2"
            className={`${styles.header} ${isMobile ? styles.headerMobile : ''}`}
          >
            {stepData?.title}
            <img
              src={infoThemeIcon}
              alt="info"
              className={`${styles.infoIcon} ${isMobile ? styles.infoIconMobile : ''}`}
              onClick={handleInfoClick}
            />
          </Text>
          {stepData?.subtitle && <p className={styles.subHeader}>{stepData.subtitle}</p>}
        </div>
      </div>

      <div className={styles.locationsContainer}>
        {locationData?.map((location, i) => (
          <div
            key={location.id}
            className={`${styles.locationCard} ${
              selectedLocation === location.id ? styles.selectedCard : ''
            }`}
            onClick={() => handleSelectLocation(location.id)}
          >
            <div
              className={`${styles.imageContainer} ${
                selectedLocation === location.id ? styles.selectedImageContainer : ''
              }`}
            >
              <div
                className={`${styles.backgroundImage} ${
                  selectedLocation === location.id ? styles.selectedBackground : ''
                }`}
                onMouseEnter={() => handleMouseEnter(i)}
                onMouseLeave={handleMouseLeave}
                style={{
                  backgroundImage: `url(${
                    i === hoveredIndex || (isMobile && selectedLocation === location.id)
                      ? location.subMediaUrl
                      : location.mediaUrl
                  })`,
                }}
              >
                <div className={styles.heartIconContainer}>
                  <img
                    src={
                      selectedLocation === location.id
                        ? favoriteIconOrange
                        : favoriteIconBlack
                    }
                    alt="favorite"
                    className={styles.heartIcon}
                  />
                </div>
                <div
                  className={`${styles.overlay} ${isMobile ? styles.mobile : ''} ${
                    isSmallMobile ? styles.smallMob : ''
                  } ${isLargeMobile ? styles.largeMob : ''}`}
                >
                  <h1
                    className={`${styles.clubName} ${isMobile ? styles.mobile : ''} ${
                      isSmallMobile ? styles.smallMob : ''
                    } ${isLargeMobile ? styles.largeMob : ''}`}
                  >
                    {location.displayName}
                  </h1>
                  <p
                    className={`${styles.clubDistance} ${isMobile ? styles.mobile : ''} ${
                      isSmallMobile ? styles.smallMob : ''
                    } ${isLargeMobile ? styles.largeMob : ''}`}
                  >
                    {isMobile ? (
                      // Mobile: Show street and city on separate lines only when selected
                      selectedLocation === location.id &&
                      (location.streetC || location.cityC) ? (
                        <>
                          <p>{location.streetC} </p>
                          <p className={styles.city}> {location.cityC}</p>
                        </>
                      ) : null
                    ) : // Desktop: Show full address from subLabel only on hover
                    i === hoveredIndex && location.subLabel ? (
                      location.subLabel
                    ) : null}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className={styles.buttonsWrapper}>
        {canSkip ? (
          // Show Skip + Next buttons when skippable
          <div className={styles.buttonGroup}>
            <button type="button" className={styles.skipButton} onClick={handleSkipClick}>
              Skip
            </button>
            <button
              type="button"
              className={styles.nextButton}
              disabled={!isNextButtonEnabled()}
              onClick={handleNextClick}
            >
              Next
            </button>
          </div>
        ) : (
          // Show only Next button when not skippable (centered)
          <button
            type="button"
            className={styles.nextButton}
            disabled={!isNextButtonEnabled()}
            onClick={handleNextClick}
          >
            Next
          </button>
        )}
      </div>
      <Modal
        open={infoModalOpen}
        onClose={handleCloseInfoModal}
        aria-labelledby="favorite-location-info"
        disableScrollLock={true}
      >
        <Box style={modalStyle}>
          <div className={styles.modalHeader}>
            <div className={styles.modalContent}>
              <div
                className={styles.modalText}
                dangerouslySetInnerHTML={{
                  __html: stepData?.tooltipText,
                }}
              />
            </div>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

LocationSelectionStep.propTypes = {
  onNext: PropTypes.func,
  stepData: PropTypes.object,
  previousSelection: PropTypes.string,
  canSkip: PropTypes.bool,
};

export default LocationSelectionStep;
